/**
 * DataTable Component
 *
 * Reusable data table component with sorting, filtering, pagination, and row selection support.
 * Optimized for displaying validation results and other tabular data.
 */

import React, { useState, useMemo } from "react";
import { ChevronUp, ChevronDown, Search, Filter } from "lucide-react";
import Button from "./Button";
import { Badge } from "./Badge";
import { cn } from "../../utils/cn";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormatters } from "../../hooks/useFormatters";

/**
 * Table header cell with sorting capability
 */
const TableHeader = ({ column, sortBy, sortOrder, onSort, className }) => {
  const isSorted = sortBy === column.key;
  const isAscending = isSorted && sortOrder === "asc";
  const isDescending = isSorted && sortOrder === "desc";
  const { t } = useLanguage();

  const handleSort = () => {
    if (column.sortable !== false) {
      const newOrder = isSorted && sortOrder === "asc" ? "desc" : "asc";
      onSort(column.key, newOrder);
    }
  };

  return (
    <th
      className={cn(
        "px-4 py-3 text-left font-semibold text-muted-foreground",
        column.sortable !== false && "cursor-pointer hover:bg-muted/70 select-none",
        className
      )}
      style={{ minWidth: column.minWidth }}
      onClick={handleSort}
    >
      <div className="flex items-center space-x-1">
        <span>{column.header}</span>
        {column.sortable !== false && (
          <div className="flex flex-col">
            <ChevronUp
              className={cn(
                "h-3 w-3 -mb-1",
                isAscending ? "text-primary" : "text-muted-foreground/50"
              )}
            />
            <ChevronDown
              className={cn(
                "h-3 w-3",
                isDescending ? "text-primary" : "text-muted-foreground/50"
              )}
            />
          </div>
        )}
      </div>
    </th>
  );
}; /**
 * Ta
ble row component with hover effects
 */
const TableRow = ({
  item,
  columns,
  onRowClick,
  className,
  selectable,
  isSelected,
  onSelect,
}) => {
  return (
    <tr
      className={cn(
        "border-b border-border hover:bg-muted/50 transition-colors",
        onRowClick && "cursor-pointer",
        isSelected && "bg-primary/10",
        className
      )}
      onClick={() => onRowClick && onRowClick(item)}
    >
      {selectable && (
        <td className="px-4 py-3">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
          />
        </td>
      )}
      {columns.map((column) => (
        <td
          key={column.key}
          className="px-4 py-3 text-sm"
          style={{ minWidth: column.minWidth }}>
          {column.render
            ? column.render(item[column.key], item)
            : item[column.key]}
        </td>
      ))}
    </tr>
  );
};

/**
 * Pagination component
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const { t } = useLanguage();
  const { formatNumber } = useFormatters();

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        <span>{t("pagination.show", "Show")}</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-border rounded px-2 py-1 text-sm bg-muted"
        >
          <option value={10}>10</option>
          <option value={15}>15</option>
          <option value={20}>20</option>
          <option value={25}>25</option>
          <option value={30}>30</option>
        </select>
        <span>
          {t("pagination.of")} {formatNumber(totalItems)}{" "}
          {t("pagination.results")}
        </span>
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-sm text-muted-foreground">
          {t("pagination.selectPages")}
        </span>
        <select
          value={currentPage}
          onChange={(e) => onPageChange(Number(e.target.value))}
          className="border border-border rounded px-2 py-1 text-sm bg-muted"
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <option key={page} value={page}>
              {page}
            </option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          {startItem}-{endItem} {t("pagination.of")} {formatNumber(totalItems)}
        </span>

        <div className="flex items-center space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            {t("pagination.previous", "Previous")}
          </Button>

          {/* Page numbers */}
          {(() => {
            const pageButtons = [];
            if (totalPages <= 5) {
              for (let i = 1; i <= totalPages; i++) {
                pageButtons.push(
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                  >
                    {i}
                  </Button>
                );
              }
            } else {
              if (currentPage < totalPages - 4) {
                // Show first 3 pages
                for (let i = currentPage; i <= currentPage + 2; i++) {
                  pageButtons.push(
                    <Button
                      key={i}
                      variant={currentPage === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(i)}
                    >
                      {i}
                    </Button>
                  );
                }
                pageButtons.push(
                  <span
                    key="ellipsis-start"
                    className="px-2 text-sm text-muted-foreground"
                  >
                    ...
                  </span>
                );
                // Show last 2 pages
                for (let i = totalPages - 1; i <= totalPages; i++) {
                  pageButtons.push(
                    <Button
                      key={i}
                      variant={currentPage === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(i)}
                    >
                      {i}
                    </Button>
                  );
                }
              } else {
                // Show last 5 pages
                for (let i = totalPages - 4; i <= totalPages; i++) {
                  pageButtons.push(
                    <Button
                      key={i}
                      variant={currentPage === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => onPageChange(i)}
                    >
                      {i}
                    </Button>
                  );
                }
              }
            }
            return pageButtons;
          })()}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            {t("pagination.next", "Next")}
          </Button>
        </div>
      </div>
    </div>
  );
}; /**
 *
 Main DataTable component
 */
const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  error = null,
  sortBy = null,
  sortOrder = "asc",
  onSort = () => {},
  onRowClick = null,
  pagination = true,
  itemsPerPage = 25,
  selectable = false,
  selectedRows = [],
  onSelectedRowsChange = () => {},
  searchable = false,
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  customPagination = null,
  className = "",
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(itemsPerPage);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm || !searchable) return data;
    return data.filter((item) =>
      columns.some((column) => {
        const value = item[column.key];
        if (value == null) return false;
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns, searchable]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / currentItemsPerPage);
  const startIndex = (currentPage - 1) * currentItemsPerPage;
  const endIndex = startIndex + currentItemsPerPage;
  const paginatedData = pagination
    ? filteredData.slice(startIndex, endIndex)
    : filteredData;

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setCurrentItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSelectAll = () => {
    const currentIds = paginatedData.map(
      (item) => item.id || JSON.stringify(item)
    );
    const selectedIds = new Set(
      selectedRows.map((item) => item.id || JSON.stringify(item))
    );

    const allOnPageSelected = currentIds.every((id) => selectedIds.has(id));

    let newSelectedRows;
    if (allOnPageSelected) {
      // Deselect all on the current page
      newSelectedRows = selectedRows.filter(
        (item) => !selectedIds.has(item.id || JSON.stringify(item))
      );
    } 
    else {
      // Select all on the current page that aren't already selected
      const newItemsToAdd = paginatedData.filter(
        (item) => !selectedIds.has(item.id || JSON.stringify(item))
      );
      newSelectedRows = [...selectedRows, ...newItemsToAdd];
    }
    onSelectedRowsChange(newSelectedRows);
  };

  const handleSelectRow = (row) => {
    const isSelected = selectedRows.some(
      (selected) =>
        (selected.id || JSON.stringify(selected)) ===
        (row.id || JSON.stringify(row))
    );
    const newSelectedRows = isSelected
      ? selectedRows.filter(
          (selected) =>
            (selected.id || JSON.stringify(selected)) !==
            (row.id || JSON.stringify(row))
        )
      : [...selectedRows, row];
    onSelectedRowsChange(newSelectedRows);
  };

  const handleSelectAllData = () => {
    if (onSelectAll) {
      onSelectAll();
    }
  };
  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="text-destructive mb-2">Error loading data</div>
        <div className="text-sm text-muted-foreground">{error}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg overflow-auto",
        className
      )}
    >
      {/* Search bar */}
      {searchable && (
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {selectable && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    onChange={handleSelectAll}
                    checked={
                      paginatedData.length > 0 &&
                      paginatedData.every((item) =>
                        selectedRows.some(
                          (sel) =>
                            (sel.id || JSON.stringify(sel)) ===
                            (item.id || JSON.stringify(item))
                        )
                      )
                    }
                    ref={(input) => {
                      if (input)
                        input.indeterminate =
                          paginatedData.length > 0 &&
                          paginatedData.some((item) =>
                            selectedRows.some(
                              (sel) =>
                                (sel.id || JSON.stringify(sel)) ===
                                (item.id || JSON.stringify(item))
                            )
                          ) &&
                          !paginatedData.every((item) =>
                            selectedRows.some(
                              (sel) =>
                                (sel.id || JSON.stringify(sel)) ===
                                (item.id || JSON.stringify(item))
                            )
                          );
                    }}
                  />
                </th>
              )}
              {columns.map((column) => (
                <TableHeader
                  key={column.key}
                  column={column}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => {
                const isSelected = selectedRows.some(
                  (sel) =>
                    (sel.id || JSON.stringify(sel)) ===
                    (item.id || JSON.stringify(item))
                );
                return (
                  <TableRow
                    key={item.id || index}
                    item={item}
                    columns={columns}
                    onRowClick={onRowClick}
                    selectable={selectable}
                    isSelected={isSelected}
                    onSelect={() => handleSelectRow(item)}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalItems > 0 && (
        <Pagination
          currentPage={
            customPagination ? customPagination.currentPage : currentPage
          }
          totalPages={
            customPagination ? customPagination.totalPages : totalPages
          }
          totalItems={
            customPagination ? customPagination.totalItems : totalItems
          }
          itemsPerPage={
            customPagination
              ? customPagination.itemsPerPage
              : currentItemsPerPage
          }
          onPageChange={
            customPagination ? customPagination.onPageChange : handlePageChange
          }
          onItemsPerPageChange={
            customPagination
              ? customPagination.onItemsPerPageChange
              : handleItemsPerPageChange
          }
        />
      )}
    </div>
  );
};

export default DataTable;
